import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { appConfirm } from '../../../components/AppConfirmModal'
import {
  fetchMarksRoster,
  saveMarks,
  deleteMarks,
} from './api/testApi'

const ORDER_OPTIONS = [
  { value: 0, label: 'Roll No.' },
  { value: 1, label: 'Branch + Roll No.' },
  { value: 2, label: 'Batch + Roll No.' },
  { value: 3, label: 'Name' },
  { value: 4, label: 'Branch + Attendance + Roll No.' },
]

const ATTENDANCE_OPTIONS = [
  { value: 'P', label: 'Present' },
  { value: 'A', label: 'Absent' },
  { value: 'S', label: 'Sick' },
  { value: 'C', label: 'Copied' },
]

const TIPS = [
  'Students are automatically shown in this list with 0 marks, based on Test Parameters. If any students are missing in this list, you can edit the Test parameters and click on the [Refresh Students List] button to refresh the list.',
  'Enter marks scored by all students who have taken the test.',
  'You can delete names of the students (if any), for whom the test is not applicable.',
  'Students who have not appeared for test can be marked Absent or Sick',
  'Any student caught copying during tests can be marked separately',
  'Marks should be less than or equal to Maximum Marks',
  'You can also enter -ve marks, if required',
]

export default function TestMarks() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const testId = searchParams.get('id')

  const [order, setOrder] = useState(0)
  const [populateMarks, setPopulateMarks] = useState('A')
  const [testMeta, setTestMeta] = useState(null)
  const [students, setStudents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [excelFile, setExcelFile] = useState(null)

  const loadRoster = useCallback(async () => {
    if (!testId) return
    setIsLoading(true)
    try {
      const data = await fetchMarksRoster(testId, populateMarks, order)
      setTestMeta({
        testId: data.testId,
        testNo: data.testNo,
        testDate: data.testDate,
        maxMarks: data.maxMarks,
        minMarks: data.minMarks,
        courseName: data.courseName,
        subjectName: data.subjectName,
        topic: data.topic,
      })
      setStudents(data.students || [])
    } catch (err) {
      appToast.error(err?.message || 'Failed to load student marks roster.')
    } finally {
      setIsLoading(false)
    }
  }, [testId, populateMarks, order])

  useEffect(() => {
    loadRoster()
  }, [loadRoster])

  const handleMarksChange = (studentId, value) => {
    setStudents((prev) =>
      prev.map((s) => {
        const id = s.studentId ?? s.id
        if (id === studentId) {
          return {
            ...s,
            marks: value,
          }
        }
        return s
      })
    )
  }

  const handleAttendanceChange = (studentId, value) => {
    setStudents((prev) =>
      prev.map((s) => {
        const id = s.studentId ?? s.id
        if (id === studentId) {
          return { ...s, attendance: value }
        }
        return s
      })
    )
  }

  const handleDeleteStudentRow = async (studentId, studentName) => {
    const confirmed = await appConfirm({
      title: 'Remove Student',
      message: `Do you wish to remove student "${studentName}" from this test evaluation?`,
      confirmText: 'Remove',
      variant: 'danger',
    })
    if (!confirmed) return
    setStudents((prev) => prev.filter((s) => (s.studentId ?? s.id) !== studentId))
    appToast.info('Student removed from current evaluation roster.')
  }

  const handleRefreshStudentsList = async () => {
    const confirmed = await appConfirm({
      title: 'Refresh Student List',
      message: 'Do you wish to reload student names from test batch parameters?',
      confirmText: 'Refresh',
      variant: 'primary',
    })
    if (!confirmed) return
    loadRoster()
    appToast.success('Student roster refreshed.')
  }

  const handleReadSwipes = async () => {
    const confirmed = await appConfirm({
      title: 'Update from Biometrics',
      message: 'Do you wish to update Test Attendance from Biometric device punch records?',
      confirmText: 'Update',
      variant: 'primary',
    })
    if (!confirmed) return
    setStudents((prev) =>
      prev.map((s) => ({
        ...s,
        attendance: s.attendance === 'A' ? 'P' : s.attendance,
      }))
    )
    appToast.success('Biometric swipes evaluated. Attendance updated from device logs.')
  }

  const handleReadMarksFile = () => {
    if (!excelFile) {
      appToast.warning('Please choose an Excel file to read marks from.')
      return
    }
    appToast.info('Reading marks from uploaded file...')
    setTimeout(() => {
      appToast.success(`Imported marks data from ${excelFile.name}.`)
      setExcelFile(null)
    }, 600)
  }

  const handleSave = async () => {
    if (!testId) return
    setIsSaving(true)
    try {
      const maxM = Number(testMeta?.maxMarks || 100)
      for (const s of students) {
        const m = typeof s.marks === 'number' ? s.marks : parseFloat(s.marks) || 0
        if (m > maxM) {
          throw new Error(`Marks for ${s.fullName} (${m}) cannot exceed Maximum Marks (${maxM}).`)
        }
      }

      const payload = students.map((s) => ({
        studentId: s.studentId ?? s.id,
        marks: typeof s.marks === 'number' ? s.marks : parseFloat(s.marks) || 0,
        attendance: s.attendance || 'P',
        remark: s.remark || '',
      }))

      await saveMarks(testId, payload)
      appToast.success('Marks saved successfully.')
      navigate('/education/tests')
    } catch (err) {
      appToast.error(err?.message || 'Failed to save marks.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteAllMarks = async () => {
    const confirmed = await appConfirm({
      title: 'Delete All Marks',
      message: 'Are you sure you want to delete all marks for this test? This action cannot be undone.',
      confirmText: 'Delete All',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsSaving(true)
    try {
      await deleteMarks(testId)
      appToast.success('Test marks deleted successfully.')
      loadRoster()
    } catch (err) {
      appToast.error(err?.message || 'Failed to delete marks.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetMarks = async () => {
    const confirmed = await appConfirm({
      title: 'Reset Marks',
      message: 'Reset all student marks to 0.00?',
      confirmText: 'Reset',
      variant: 'warning',
    })
    if (!confirmed) return
    setStudents((prev) => prev.map((s) => ({ ...s, marks: 0.0 })))
    appToast.info('Marks reset to 0.00 in current view.')
  }

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-5 pb-24">
      {/* ── Top Toolbar: Order Dropdown & Header Actions (Matching Image 3) ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-on-surface">Order:</label>
          <select
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            className="rounded-xl border border-outline-variant/30 bg-surface px-3 py-1.5 text-xs font-semibold text-on-surface focus:border-primary focus:outline-none"
          >
            {ORDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefreshStudentsList}
            title="Refresh Students List"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-outline-variant/30 bg-surface text-primary hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">refresh</span>
          </button>
          <button
            type="button"
            onClick={handleReadSwipes}
            title="Read Biometric Swipes"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-outline-variant/30 bg-surface text-primary hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">contactless</span>
          </button>
        </div>
      </div>

      {/* ── Main Layout: Left Roster Card + Right TIPS (Matching Image 3) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Marks Roster */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm">
            {/* Blue Banner Header (Matching Image 3) */}
            <div className="bg-[#198cb4] text-white px-5 py-3 flex items-center justify-between">
              <span className="font-bold text-sm tracking-wide">Marks</span>
              <span className="text-xs font-medium bg-white/20 px-2.5 py-0.5 rounded-full">
                {students.length} entries
              </span>
            </div>

            {/* Test info subtitle */}
            <div className="bg-[#e4f3f8] text-[#13617c] px-5 py-2.5 text-xs font-semibold border-b border-[#c8e4ee]">
              {testMeta ? (
                <>
                  Test No. {testMeta.testNo || testMeta.testId} conducted on {formatDateDisplay(testMeta.testDate)}
                  {testMeta.subjectName ? ` • ${testMeta.subjectName}` : ''}
                  {testMeta.topic ? ` (${testMeta.topic})` : ''}
                </>
              ) : (
                'Loading test details...'
              )}
            </div>

            {/* Students Roster Table (Matching Image 3) */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low/70 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="w-10 px-3 py-2.5 text-center" />
                    <th className="px-4 py-2.5">Branch</th>
                    <th className="px-4 py-2.5">Student Name</th>
                    <th className="px-4 py-2.5">RollNo</th>
                    <th className="px-4 py-2.5 w-28">Marks</th>
                    <th className="px-4 py-2.5 w-32">Attendance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-on-surface-variant font-medium">
                        Loading student marks roster...
                      </td>
                    </tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-on-surface-variant">
                        No students enrolled or allocated for this test.
                      </td>
                    </tr>
                  ) : (
                    students.map((s) => {
                      const id = s.studentId ?? s.id
                      return (
                        <tr key={id} className="hover:bg-surface-container-low/30 transition-colors">
                          {/* Red cross delete button (Matching Image 3) */}
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleDeleteStudentRow(id, s.fullName || s.studentName)}
                              title="Delete Student from Roster"
                              className="text-error font-extrabold hover:text-error/70 transition-colors text-sm leading-none cursor-pointer"
                            >
                              ✕
                            </button>
                          </td>

                          {/* Branch */}
                          <td className="px-4 py-2 font-medium text-on-surface">
                            {s.branchName || '—'}
                          </td>

                          {/* Student Name */}
                          <td className="px-4 py-2 font-semibold text-on-surface">
                            {s.fullName || s.studentName || '—'}
                          </td>

                          {/* RollNo */}
                          <td className="px-4 py-2 font-mono text-on-surface-variant">
                            {s.rollNo || '—'}
                          </td>

                          {/* Marks Input (Supports negative and decimals) */}
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={s.marks ?? 0.0}
                              onChange={(e) => handleMarksChange(id, e.target.value)}
                              className="w-20 rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs font-semibold text-on-surface focus:border-primary focus:outline-none"
                            />
                          </td>

                          {/* Attendance Dropdown */}
                          <td className="px-4 py-2">
                            <select
                              value={s.attendance || 'P'}
                              onChange={(e) => handleAttendanceChange(id, e.target.value)}
                              className="w-28 rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs text-on-surface focus:border-primary focus:outline-none"
                            >
                              {ATTENDANCE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Excel File Reader under table (Matching Image 3) */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 bg-surface-container-low/40 border-t border-outline-variant/15">
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                className="text-xs text-on-surface-variant file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border file:border-outline-variant/30 file:text-xs file:font-semibold file:bg-surface file:text-on-surface hover:file:bg-surface-container-high cursor-pointer"
              />
              <button
                type="button"
                onClick={handleReadMarksFile}
                className="rounded-lg border border-outline-variant/30 bg-surface px-4 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer shrink-0"
              >
                Read Marks
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: TIPS Box (Matching Image 3) */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          <section className="rounded-3xl border border-outline-variant/20 bg-slate-50/80 p-6 sticky top-6 shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-base text-primary">lightbulb</span>
              TIPS
            </h4>
            <ul className="space-y-3 text-xs text-on-surface-variant leading-relaxed">
              {TIPS.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      {/* ── Bottom Action Bar (Matching Image 3: Delete Marks, Refresh Marks | Save, Cancel) ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-between border-t border-outline-variant/20 bg-surface/95 px-6 py-3 shadow-lg backdrop-blur-md">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDeleteAllMarks}
            disabled={isSaving}
            className="rounded-xl bg-[#dc4c38] px-4 py-2 text-xs font-bold text-white hover:bg-[#c23e2c] transition-colors shadow-sm cursor-pointer disabled:opacity-50"
          >
            Delete Marks
          </button>
          <button
            type="button"
            onClick={handleResetMarks}
            disabled={isSaving}
            className="rounded-xl bg-[#3eb0c9] px-4 py-2 text-xs font-bold text-white hover:bg-[#349eb5] transition-colors shadow-sm cursor-pointer disabled:opacity-50"
          >
            Refresh Marks
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-xl bg-[#2e7d32] px-6 py-2 text-xs font-bold text-white hover:bg-[#256628] transition-colors shadow-sm cursor-pointer disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/education/tests')}
            disabled={isSaving}
            className="rounded-xl border border-outline-variant/40 bg-surface px-5 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
